import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Workspace } from "../../types/Dash";
import { Folder } from "../../types/Folder";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Reticle from "./Reticle";
import { Scenario } from "../../types/Scenario";
import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setScenarioId } from "./librarySlice";
import { selectFolders } from "./librarySlice";

export default function DashLibraryScenario(props: {
  workspaceRole: string;
  scenario: Scenario;
  workspaces: Array<Workspace> | null;
  currentWorkspace: Workspace | null;
}) {
  const dispatch = useAppDispatch();
  const actions = useActions();
  const folders = useAppSelector(selectFolders) as Folder[];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState(props.scenario.name);
  const [editFolderId, setEditFolderId] = useState<number | "">(
    props.scenario.folder_id ?? ""
  );
  const [editPriority, setEditPriority] = useState<number>(props.scenario.priority ?? 0);

  const handleClicked = () => {
    dispatch(setScenarioId(props.scenario.id));
    actions.getScenarioMeasurables(props.scenario.id);
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  const handleOpenEdit = () => {
    setEditName(props.scenario.name);
    setEditFolderId(props.scenario.folder_id ?? "");
    setEditPriority(props.scenario.priority ?? 0);
    setEditOpen(true);
    handleCloseMenu();
  };
  const handleCloseEdit = () => setEditOpen(false);

  const handleEditSave = () => {
    actions.updateScenario({
      scenarioId: props.scenario.id,
      name: editName,
      parent_scenario_id: -1,
      folder_id: editFolderId === "" ? null : (editFolderId as number),
      priority: editPriority,
    });
    handleCloseEdit();
  };

  return (
    <>
      <ImageListItem
        key={"scenario_" + props.scenario.id}
        sx={{
          justifyContent: "center",
          display: "flex",
          width: 125,
          height: 125,
        }}
      >
        <Paper
          sx={{
            background: "#CCCCCC",
            justifyContent: "center",
            display: "flex",
            width: 125,
            height: 125,
            position: "relative",
            "&:hover .folder-reticle": {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              data-index={"scenario_" + props.scenario.id}
              onClick={handleClicked}
              disableRipple
              sx={{
                "&:hover": {
                  bgcolor: "transparent",
                },
              }}
            >
              <PlayArrowIcon color="primary" sx={{ fontSize: 96 }} />
            </IconButton>
            <Reticle size={72} />
          </Box>
        </Paper>
        <ImageListItemBar
          title={props.scenario.name}
          position="below"
          actionIcon={
            <IconButton
              aria-label={`options for ${props.scenario.name}`}
              onClick={handleOpenMenu}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
          }
        />
      </ImageListItem>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleOpenEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit scenario</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit scenario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="standard"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Priority"
            type="number"
            fullWidth
            variant="standard"
            value={editPriority}
            onChange={(e) => setEditPriority(Number(e.target.value))}
            inputProps={{ step: 1, min: 0 }}
          />
          <FormControl fullWidth margin="dense" variant="standard">
            <InputLabel id="edit-scenario-folder-label">Folder</InputLabel>
            <Select
              labelId="edit-scenario-folder-label"
              value={editFolderId}
              onChange={(e) =>
                setEditFolderId(e.target.value === "" ? "" : (e.target.value as number))
              }
              label="Folder"
            >
              <MenuItem value="">None</MenuItem>
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
